/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { Box, theme } from "@nordcloud/gnui";
import { ActionSortFields, SortOrder } from "~/generated/graphql";
import { TableToolbar, TableToolbarSearch } from "~/components";
import { AppliedFiltersSection } from "~/components/AppliedFiltersSection";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import {
  minSearchLength,
  TableSearchInput,
  getNoSearchResultsMessage,
} from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { TableWrapper } from "~/components/Tables";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { useAppliedFilters } from "~/hooks/useAppliedFilters";
import { isEmpty, isNotNil } from "~/tools";
import {
  columns,
  CustomActions,
} from "~/views/customActions/components/CustomActionsTableColumns";
import { useCustomActionsAppliedFilters } from "../hooks/useCustomActionsAppliedFilters";
import { useGetCustomActions } from "../hooks/useGetCustomActions/useGetCustomActions";
import { CustomActionQuery } from "../types";
import { ActionFiltersSidebar } from "./ActionFiltersSidebar";
import { prepareCustomActionsFilter } from "./customActionFilterBuilders";

const sortOptions = [
  {
    value: ActionSortFields.Name,
    label: "Custom Actions Name",
  },
  {
    value: ActionSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: ActionSortFields.UpdatedAt,
    label: TABLE_LABELS.updatedAt,
  },
  {
    value: ActionSortFields.UpdatedBy,
    label: TABLE_LABELS.updatedBy,
  },
  {
    value: ActionSortFields.Type,
    label: TABLE_LABELS.type,
  },
  {
    value: ActionSortFields.Scope,
    label: TABLE_LABELS.scope,
  },
];

export function CustomActionsTable() {
  const { queryState, updateQueryState } = useQueryState<CustomActionQuery>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: ActionSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (field: string | undefined) => {
    updateQueryState({
      ...queryState,
      searchPhrase: field,
    });
  };

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  const { data, loading, count } = useGetCustomActions({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? ActionSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: prepareCustomActionsFilter(queryState),
    includeRelatedPlansCount: true,
  });

  const customActionAppliedFilterDefinitions = useCustomActionsAppliedFilters();
  const appliedFiltersState = useAppliedFilters({
    queryState,
    updateQueryState,
    definitions: customActionAppliedFilterDefinitions,
  });

  return (
    <>
      <Box
        boxStyle="lightGrey"
        spacing="spacing04"
        style={{ backgroundColor: theme.color.background.ui02 }}
      >
        <TableToolbar>
          <SortSelector<ActionSortFields>
            options={sortOptions}
            value={queryState.field ?? ActionSortFields.Name}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
          <TableToolbarSearch>
            <TableSearchInput<CustomActionQuery>
              onSubmit={handlePhraseField}
              placeholder="Type a Keyword (e.g. Action Name, Description, ID)"
            />
          </TableToolbarSearch>
          <ActionFiltersSidebar resultsCount={count ?? 0} />
        </TableToolbar>
        <AppliedFiltersSection state={appliedFiltersState} />
      </Box>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(data)}>
              <Then>
                <NoData hasIcon message={"There are no Custom Actions."} />
              </Then>
              <Else>
                <>
                  <TableWrapper<CustomActions>
                    loading={loading}
                    data={data.filter(isNotNil)}
                    columns={columns()}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    stripped
                  />
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
